import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as Api from "@/api";
import Avatar from "@/design/components/Avatar";
import FormGroup from "@/design/forms/FormGroup";
import { setSystemSettingsPage } from "@/store/reducers/app";
import {
  clearDashboardQueueList,
  setDashboardQueueList,
  setDashboardUserList,
  setFilterByStatus,
  toggleDashboardQueue,
  toggleDashboardUser,
  toggleGroupByStatus,
  toggleShowAllUsers,
  toggleShowQueues,
  toggleShowSetup,
} from "@/store/reducers/dashboard";
import useAccessibleColors from "@/utils/hooks/useAccessibleColors";
import Valkyrie, {
  viAngleDown,
  viGear,
  viLayers,
  viList,
  viListChecks,
  viListChecksXmark,
  viPeople,
  viPerson,
  viSpinnerThird,
  viXmark,
} from "@sippy-platform/valkyrie";

import {
  Box,
  Card,
  Checkbox,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";

export default function Setup() {
  const dispatch = useDispatch();
  const colors = useAccessibleColors();

  const auth = useSelector((state) => state.auth);
  const pbx = useSelector((state) => state.domain.pbx);
  const dashboardSettings = useSelector((state) => state.dashboard.find((_pbx) => _pbx.context === pbx.context));
  const userStore = useSelector((state) => state.domain.users);
  const queueStore = useSelector((state) => state.domain.queues);

  const canViewQueues = auth.accessLevel <= 3;

  // Fetch basic information
  useEffect(() => {
    Promise.all(canViewQueues ? [Api.getUsers(), Api.getQueues()] : [Api.getUsers()]);
  }, [canViewQueues]);

  // Options menu
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const statusMenuOpen = Boolean(statusAnchorEl);
  const openStatusMenu = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };
  const closeStatusMenu = (status) => {
    if (status) {
      dispatch(setFilterByStatus({ pbx: pbx.context, status }));
    }

    setStatusAnchorEl(null);
  };

  if (!dashboardSettings) {
    return <>Setting up dashboard</>;
  }

  return (
    <Grid size={12}>
      <FormGroup
        title="Welcome to your Dashboard"
        collapsible={true}
        actions={
          <IconButton onClick={() => dispatch(toggleShowSetup({ pbx: pbx.context }))}>
            <Valkyrie icon={viXmark} />
          </IconButton>
        }
      >
        <Grid size={{ xs: 12, xl: 3 }}>
          <Typography variant="body1" sx={{ mt: { xs: 0, xl: 1 } }}>
            Welcome to your Sippy Dashboard. This is where you can follow the ongoing calls from your users and queues.
            Let's get your dashboard set up. You can always change this later in your settings.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              mb: 1,
              height: 36,
            }}
          >
            <Typography sx={{ fontSize: 18, mb: 1, fontWeight: 600, m: 0 }}>Settings</Typography>
            <Stack
              direction="row"
              sx={{
                gap: 0.5,
              }}
            >
              <Tooltip title="Open this in Dashboard settings" placement="top">
                <span>
                  <IconButton
                    onClick={() => {
                      dispatch(setSystemSettingsPage("dashboard"));
                      dispatch(toggleShowSetup({ pbx: pbx.context }));
                    }}
                  >
                    <Valkyrie icon={viGear} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          <Card variant="outlined">
            <List sx={{ p: 1 }}>
              <ListItem disablePadding sx={{ minHeight: 40 }}>
                <ListItemButton sx={{ minHeight: 40, py: 0.5 }} onClick={openStatusMenu}>
                  <ListItemIcon>
                    <Valkyrie icon={viPerson} />
                  </ListItemIcon>
                  <ListItemText primary="Filter by status" />
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: "center",
                      gap: 0.5,
                      pl: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ m: 0 }}>
                      {dashboardSettings.settings.filterByStatus === "active"
                        ? "Available & busy"
                        : dashboardSettings.settings.filterByStatus === "inactive"
                          ? "Unavailable"
                          : "All"}
                    </Typography>
                    <Valkyrie icon={viAngleDown} />
                  </Stack>
                </ListItemButton>
                <Menu
                  anchorEl={statusAnchorEl}
                  open={statusMenuOpen}
                  onClose={() => closeStatusMenu()}
                  slotProps={{
                    paper: { elevation: 1, sx: { border: "1px solid", borderColor: "divider", minWidth: 140 } },
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={() => closeStatusMenu("all")}
                    selected={dashboardSettings.settings.filterByStatus === "all"}
                    sx={{ px: 1.5 }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "center",
                        gap: 0.5,
                        width: 32,
                        mr: 1.5,
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.green }} />
                      <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.amber }} />
                      <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.grey }} />
                    </Stack>
                    All
                  </MenuItem>
                  <MenuItem
                    onClick={() => closeStatusMenu("active")}
                    selected={dashboardSettings.settings.filterByStatus === "active"}
                    sx={{ px: 1.5 }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "center",
                        gap: 0.5,
                        width: 32,
                        mr: 1.5,
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.green }} />
                      <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.amber }} />
                    </Stack>
                    Available & busy
                  </MenuItem>
                  <MenuItem
                    onClick={() => closeStatusMenu("inactive")}
                    selected={dashboardSettings.settings.filterByStatus === "inactive"}
                    sx={{ px: 1.5 }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "center",
                        gap: 0.5,
                        width: 32,
                        mr: 1.5,
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: 4, background: colors.grey }} />
                    </Stack>
                    Unavailable
                  </MenuItem>
                </Menu>
              </ListItem>
              <ListItem disablePadding sx={{ minHeight: 40 }}>
                <ListItemButton
                  onClick={() => dispatch(toggleGroupByStatus({ pbx: pbx.context }))}
                  sx={{ minHeight: 40, py: 0.5 }}
                >
                  <ListItemIcon>
                    <Valkyrie icon={viLayers} />
                  </ListItemIcon>
                  <ListItemText primary="Group by status" />
                  <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                    <Switch checked={dashboardSettings.settings.groupByStatus} sx={{ m: 0, mt: -0.5 }} />
                  </Box>
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ minHeight: 40 }}>
                <ListItemButton
                  onClick={() => dispatch(toggleShowAllUsers({ pbx: pbx.context }))}
                  sx={{ minHeight: 40, py: 0.5 }}
                >
                  <ListItemIcon>
                    <Valkyrie icon={viPeople} />
                  </ListItemIcon>
                  <ListItemText primary="Show all users" />
                  <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                    <Switch checked={dashboardSettings.settings.showAllUsers} sx={{ m: 0, mt: -0.5 }} />
                  </Box>
                </ListItemButton>
              </ListItem>
            </List>
          </Card>
          {canViewQueues && (
            <Card variant="outlined" sx={{ mt: 1 }}>
              <List sx={{ p: 1 }}>
                <ListItem disablePadding sx={{ minHeight: 40 }}>
                  <ListItemButton
                    onClick={() => dispatch(toggleShowQueues({ pbx: pbx.context }))}
                    sx={{ minHeight: 40, py: 0.5 }}
                  >
                    <ListItemIcon>
                      <Valkyrie icon={viList} />
                    </ListItemIcon>
                    <ListItemText primary="Show queues" />
                    <Box sx={{ p: 0, justifyContent: "flex-end", pl: 1 }}>
                      <Switch checked={dashboardSettings.settings.showQueues} sx={{ m: 0, mt: -0.5 }} />
                    </Box>
                  </ListItemButton>
                </ListItem>
              </List>
            </Card>
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: canViewQueues ? 4 : 8, xl: canViewQueues ? 3 : 6 }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              mb: 1,
              height: 36,
            }}
          >
            <Typography sx={{ fontSize: 18, mb: 1, fontWeight: 600, m: 0 }}>Users</Typography>
            <Stack
              direction="row"
              sx={{
                gap: 0.5,
              }}
            >
              <Tooltip title="Select all" placement="top">
                <span>
                  <IconButton
                    disabled={
                      dashboardSettings.settings.showAllUsers ||
                      dashboardSettings.userList.length === userStore.list.length
                    }
                    onClick={() =>
                      dispatch(
                        setDashboardUserList({ pbx: pbx.context, list: userStore.list.map((_user) => _user.id) }),
                      )
                    }
                  >
                    <Valkyrie icon={viListChecks} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear selection" placement="top">
                <span>
                  <IconButton
                    disabled={dashboardSettings.settings.showAllUsers || dashboardSettings.userList.length === 0}
                    onClick={() => dispatch(setDashboardUserList({ pbx: pbx.context, list: [] }))}
                  >
                    <Valkyrie icon={viListChecksXmark} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          <Card variant="outlined">
            <List sx={(theme) => ({ p: 1, minHeight: 206, maxHeight: 320, overflow: "auto", ...theme.scrollbar })}>
              {userStore.meta.lastFetched === null ? (
                <Stack
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    textAlign: "center",
                    px: 1.5,
                    lineHeight: 1,
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 32,
                      color: "primary.500",
                      m: 0,
                    }}
                  >
                    <Valkyrie icon={viSpinnerThird} spin />
                  </Box>
                  <Typography sx={{ m: 0, fontWeight: 400 }} variant="h5">
                    Getting your users...
                  </Typography>
                </Stack>
              ) : userStore.meta.count === 0 ? (
                <Stack
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    textAlign: "center",
                    px: 1.5,
                    lineHeight: 1,
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 32,
                      color: "primary.500",
                      m: 0,
                    }}
                  >
                    <Valkyrie icon={viPerson} />
                  </Box>
                  <Typography sx={{ m: 0, fontWeight: 400 }} variant="h5">
                    You don't have any users.
                  </Typography>
                </Stack>
              ) : (
                userStore.list.map((user) => (
                  <ListItem
                    key={user.id}
                    disablePadding
                    sx={{ minHeight: 40 }}
                    secondaryAction={
                      <Checkbox
                        size="small"
                        tabIndex={-1}
                        edge="end"
                        disableRipple
                        disabled={dashboardSettings.settings.showAllUsers}
                        checked={dashboardSettings.userList.includes(user.id)}
                        onClick={() => dispatch(toggleDashboardUser({ pbx: pbx.context, userId: user.id }))}
                      />
                    }
                  >
                    <ListItemButton
                      sx={{ minHeight: 40, py: 0.5 }}
                      disabled={dashboardSettings.settings.showAllUsers}
                      onClick={() => dispatch(toggleDashboardUser({ pbx: pbx.context, userId: user.id }))}
                    >
                      <ListItemAvatar sx={{ minWidth: 32 }}>
                        <Avatar text={user.name} px={24} />
                      </ListItemAvatar>
                      <ListItemText primary={user.name} />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Card>
        </Grid>

        {canViewQueues && (
          <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                mb: 1,
                height: 36,
              }}
            >
              <Typography sx={{ fontSize: 18, mb: 1, fontWeight: 600, m: 0 }}>Queues</Typography>
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                }}
              >
                <Tooltip title="Select all" placement="top">
                  <span>
                    <IconButton
                      disabled={
                        !dashboardSettings.settings.showQueues ||
                        dashboardSettings.queueList.length >= 20 ||
                        dashboardSettings.queueList.length === queueStore.list.length
                      }
                      onClick={() =>
                        dispatch(
                          setDashboardQueueList({ pbx: pbx.context, list: queueStore.list.map((_queue) => _queue.id) }),
                        )
                      }
                    >
                      <Valkyrie icon={viListChecks} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Clear selection" placement="top">
                  <span>
                    <IconButton
                      disabled={!dashboardSettings.settings.showQueues || dashboardSettings.queueList.length === 0}
                      onClick={() => dispatch(clearDashboardQueueList({ pbx: pbx.context }))}
                    >
                      <Valkyrie icon={viListChecksXmark} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
            <Card variant="outlined">
              <List sx={(theme) => ({ p: 1, minHeight: 206, maxHeight: 320, overflow: "auto", ...theme.scrollbar })}>
                {queueStore.meta.lastFetched === null ? (
                  <Stack
                    sx={{
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      textAlign: "center",
                      px: 1.5,
                      lineHeight: 1,
                      flexGrow: 1,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 32,
                        color: "primary.500",
                        m: 0,
                      }}
                    >
                      <Valkyrie icon={viSpinnerThird} spin />
                    </Box>
                    <Typography sx={{ m: 0, fontWeight: 400 }} variant="h5">
                      Getting your queues...
                    </Typography>
                  </Stack>
                ) : queueStore.meta.count === 0 ? (
                  <Stack
                    sx={{
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      textAlign: "center",
                      px: 1.5,
                      lineHeight: 1,
                      flexGrow: 1,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 32,
                        color: "primary.500",
                        m: 0,
                      }}
                    >
                      <Valkyrie icon={viList} />
                    </Box>
                    <Typography sx={{ m: 0, fontWeight: 400 }} variant="h5">
                      You don't have any queues.
                    </Typography>
                  </Stack>
                ) : (
                  queueStore.list.map((queue) => (
                    <ListItem
                      key={queue.id}
                      disablePadding
                      sx={{ minHeight: 40 }}
                      secondaryAction={
                        <Checkbox
                          size="small"
                          tabIndex={-1}
                          edge="end"
                          disableRipple
                          disabled={!dashboardSettings.settings.showQueues}
                          checked={!!dashboardSettings.queueList.find((_queue) => _queue.id === queue.id)}
                          onClick={() => dispatch(toggleDashboardQueue({ pbx: pbx.context, queueId: queue.id }))}
                        />
                      }
                    >
                      <ListItemButton
                        sx={{ minHeight: 40, py: 0.5 }}
                        disabled={!dashboardSettings.settings.showQueues}
                        onClick={() => dispatch(toggleDashboardQueue({ pbx: pbx.context, queueId: queue.id }))}
                      >
                        <ListItemAvatar sx={{ minWidth: 32 }}>
                          <Avatar text={queue.name} icon={viList} px={24} />
                        </ListItemAvatar>
                        <ListItemText primary={queue.name} />
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </List>
            </Card>
          </Grid>
        )}
      </FormGroup>
    </Grid>
  );
}
